import { setdefault } from "./util";
class BatchContext {
    mCloseCBs = [];
    mId;
    mKeys;
    mValues = {};
    mCompletion;
    constructor(id, keys) {
        this.mId = id;
        this.mKeys = keys;
        this.mCompletion = new Promise((resolve) => {
            this.onClose(() => resolve());
        });
    }
    get itemCount() {
        return this.mKeys.length;
    }
    await() {
        return this.mCompletion;
    }
    get(varName, fallback) {
        return this.mValues[varName] ?? fallback;
    }
    set(varName, value) {
        this.mValues[varName] = value;
    }
    close() {
        this.mCloseCBs.forEach((cb) => cb(this));
    }
    onClose(cb) {
        this.mCloseCBs.push(cb);
    }
}
const contexts = {};
function makeKey(id, key) {
    return `${id}-${key}`;
}
function previousBatch(keys, context) {
    for (const key of keys) {
        if (contexts[key] !== undefined && contexts[key][0] !== context) {
            return contexts[key][0];
        }
    }
    return undefined;
}
export function getBatchContext(operation, key, create = false) {
    if (Array.isArray(operation)) {
        // We don't create contexts for multiple operations, only search.
        for (const op of operation) {
            const res = getBatchContext(op, key, false);
            if (res !== undefined) {
                return res;
            }
        }
        return undefined;
    }
    const fullKey = makeKey(operation, key);
    const res = contexts[fullKey];
    if (res !== undefined) {
        return res[0];
    }
    else {
        if (create) {
            const newContext = new BatchContext(operation, [key]);
            contexts[fullKey] = [newContext];
            return newContext;
        }
        return undefined;
    }
}
export async function withBatchContext(operation, keys, cb) {
    const context = new BatchContext(operation, keys);
    const fullKeys = keys.map((key) => makeKey(operation, key));
    for (const key of fullKeys) {
        setdefault(contexts, key, []).push(context);
    }
    // ensure we're not processing another batch of the same operation including this key
    // concurrently
    // This is necessary as the contexts are globals, fortunately it should be really rare
    // this becomes relevant
    let preBatch = previousBatch(fullKeys, context);
    while (preBatch !== undefined) {
        await preBatch.await();
        preBatch = previousBatch(fullKeys, context);
    }
    let res;
    try {
        res = await cb();
    }
    finally {
        context.close();
        fullKeys.forEach((key) => {
            const idx = contexts[key].indexOf(context);
            // idx should *always* be 0 at this point, the block above ensures we only run a batch
            // when it's at idx 0 for all keys
            contexts[key].splice(idx, idx + 1);
        });
    }
    return res;
}
